import { useSelector, useDispatch } from "react-redux";
import {
  Grid,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Select,
  InputAdornment,
  FormControlLabel,
  Switch,
  useMediaQuery,
  Box,
  Snackbar,
  Alert,
  FormControl,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  setSettings,
  setCalculated,
} from "../../features/strategySlice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { memo, useCallback, useEffect, useState } from "react";
import theme from "../../theme";

const StrategySettings = memo(
  ({
    settingsExpanded,
    setSettingsExpanded,
    strategyId,
    assetStartYear,
    assetEndYear,
    strategy,
  }) => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const globalSettings = useSelector((state) => state.strategy.settings);
    const strategyByIdStatus = useSelector((state) => state.strategy.strategyByIdStatus); // ✅ Loading state
    const [investmentError, setInvestmentError] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");
    const [dateRangeError, setDateRangeError] = useState(false);

    const isLoadingStrategyData = strategyByIdStatus === 'loading';

    useEffect(() => {
      const applyYearPickerStyling = () => {
        const yearButtons = document.querySelectorAll('.MuiPickersYear-yearButton');
        yearButtons.forEach(button => {
          button.style.setProperty('line-height', '28px', 'important');

        });
      };

      const observer = new MutationObserver(() => {
        applyYearPickerStyling();
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }, []);

    // Currency options
    const currencies = [
      { code: 'GBP', symbol: '£', name: 'British Pound' },
      { code: 'USD', symbol: '$', name: 'US Dollar' },
      { code: 'EUR', symbol: '€', name: 'Euro' }
    ];

    const [localSettings, setLocalSettings] = useState({
        startDate: new Date(assetStartYear ? `${assetStartYear}-01-15` : '2010-01-15'),
    endDate: new Date(assetEndYear ? `${assetEndYear}-12-15` : '2020-12-15'),
      investmentAmount: "",
      rebalancingPeriod: 0,
      transactionFeeValue: "",
      transactionFeeType: "-",
      currency: "GBP", // Default currency
    });

        useEffect(() => {

        setLocalSettings(prevLocalSettings => {
          const newStartDate = globalSettings.startDate ? new Date(globalSettings.startDate) : prevLocalSettings.startDate;
          const newEndDate = globalSettings.endDate ? new Date(globalSettings.endDate) : prevLocalSettings.endDate;

          const newSettings = {
            investmentAmount: globalSettings.investmentAmount || "1000",
            rebalancingPeriod: globalSettings.rebalancingPeriod || 0,
            transactionFeeValue: globalSettings.transactionFeeValue || "",
            transactionFeeType:
              globalSettings.rebalancingPeriod === 0
                ? "-"
                : globalSettings.transactionFeeType || "-",
            startDate: newStartDate,
            endDate: newEndDate,
            currency: globalSettings.currency || "GBP",
          };

          return newSettings;
        });
      }, [globalSettings]);

    const handleSettingsToggle = useCallback(() => {
      setSettingsExpanded(false);
    }, [setSettingsExpanded]);

    const isValidDate = (date) => {
        return date instanceof Date && !isNaN(date);
      };

    const handleDateChange = (field) => (newValue) => {
    if (!isValidDate(newValue)) {
      return;
    }

    const minDate = getMinDate();
    const maxDate = getMaxDate();

    if (newValue < minDate || newValue > maxDate) {
      const currency = localSettings.currency;
      const minDateStr = minDate.toLocaleDateString('en-GB');
      const maxDateStr = maxDate.toLocaleDateString('en-GB');

      setSnackbarMessage(`Date must be between ${minDateStr} and ${maxDateStr} for ${currency} currency`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    setLocalSettings(prev => ({ ...prev, [field]: newValue }));
    dispatch(setCalculated(false));
    // Clear date range error when user changes dates
    setDateRangeError(false);
  };

        const handleCurrencyChange = (event) => {
      const newCurrency = event.target.value;

      // Get the start and end dates for the new currency from strategy data
      let updatedSettings = { currency: newCurrency };

      if (strategy && strategy.start_dates && strategy.end_dates) {
        const newStartDate = strategy.start_dates[newCurrency];
        const newEndDate = strategy.end_dates[newCurrency];

        if (newStartDate && newEndDate) {
          updatedSettings.startDate = new Date(newStartDate);
          updatedSettings.endDate = new Date(newEndDate);
        } else {

                    const currentStartDate = localSettings.startDate;
          const currentEndDate = localSettings.endDate;

          const minDate = getMinDateForCurrency(newCurrency);
          const maxDate = getMaxDateForCurrency(newCurrency);

          if (currentStartDate < minDate) {
            updatedSettings.startDate = minDate;
          }
          if (currentEndDate > maxDate) {
            updatedSettings.endDate = maxDate;
          }
        }
            } else {
      }

      setLocalSettings(prev => {
        const newSettings = { ...prev, ...updatedSettings };
        return newSettings;
      });

      // REMOVED: Don't update global Redux settings immediately
      // The global settings will be updated only when user clicks "Calculate"
      // This prevents automatic API calls when currency changes

      dispatch(setCalculated(false));
    };

    // Get current currency details
    const getCurrentCurrency = () => {
      return currencies.find(curr => curr.code === localSettings.currency) || currencies[0];
    };

    const getMinDate = () => {
      const currency = localSettings.currency;
      return getMinDateForCurrency(currency);
    };

    const getMaxDate = () => {
      const currency = localSettings.currency;
      return getMaxDateForCurrency(currency);
    };

    const getMinDateForCurrency = (currency) => {
      if (strategy && strategy.start_dates && strategy.start_dates[currency]) {
        return new Date(strategy.start_dates[currency]);
      }
      return new Date(`${assetStartYear || 2000}-01-01`);
    };

    const getMaxDateForCurrency = (currency) => {
      if (strategy && strategy.end_dates && strategy.end_dates[currency]) {
        return new Date(strategy.end_dates[currency]);
      }
      return new Date();
    };



    const handleInputChange = (field) => (e) => {
        const value = e.target.value;

        setLocalSettings((prev) => {
          let updatedSettings = { ...prev, [field]: value };

          if (field === "investmentAmount") {
            const investmentAmount = parseFloat(value);
            if (investmentAmount <= 0 || isNaN(investmentAmount)) {
              setInvestmentError("Initial investment must be greater than 0");
            } else {
              setInvestmentError("");
            }
          }

          if (field === "rebalancingPeriod") {
            const rebalancingPeriod = parseInt(value, 10);
            if (rebalancingPeriod === 0 || isNaN(rebalancingPeriod)) {
              updatedSettings.transactionFeeType = "-";
              updatedSettings.transactionFeeValue = "";
            }
          }

          if (field === "transactionFeeType") {
            if (value === "-") {
              updatedSettings.transactionFeeValue = "";
            } else if (!updatedSettings.transactionFeeValue) {
              updatedSettings.transactionFeeValue = "1";
            }
          }

          return updatedSettings;
        });
        dispatch(setCalculated(false));
      };

    // Helper function to adjust dates if they fall on weekends or might have missing data
    const adjustDatesForBackendCompatibility = (startDate, endDate) => {
      let adjustedStartDate = new Date(startDate);
      let adjustedEndDate = new Date(endDate);

      // Helper function to check if date is weekend
      const isWeekend = (date) => {
        const dayOfWeek = date.getDay();
        return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
      };

      // Adjust start date: if weekend, move to previous Friday
      while (isWeekend(adjustedStartDate)) {
        adjustedStartDate.setDate(adjustedStartDate.getDate() - 1);
      }

      // Adjust end date: if weekend, move to previous Friday
      while (isWeekend(adjustedEndDate)) {
        adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
      }

      // Additional safety: if dates are very recent, move them back a few days
      // to avoid potential missing currency data
      const today = new Date();
      const daysDiff = Math.floor((today - adjustedEndDate) / (1000 * 60 * 60 * 24));

      if (daysDiff < 7) { // If end date is less than a week ago
        adjustedEndDate.setDate(adjustedEndDate.getDate() - (7 - daysDiff));
        if (adjustedStartDate > adjustedEndDate) {
          adjustedStartDate.setDate(adjustedEndDate.getDate() - 30);
        }
      }

      return {
        startDate: adjustedStartDate.toISOString().split("T")[0],
        endDate: adjustedEndDate.toISOString().split("T")[0]
      };
    };

    const handleRecalculate = () => {
        const investmentAmount = parseFloat(localSettings.investmentAmount);
        if (investmentAmount <= 0) {
          setInvestmentError("Initial investment must be greater than 0");
          return;
        } else {
          setInvestmentError("");
        }

        // Check if date range is valid FIRST before processing
        const startDate = new Date(localSettings.startDate);
        const endDate = new Date(localSettings.endDate);
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = timeDiff / (1000 * 3600 * 24);

        if (daysDiff < 90) { // Less than 3 months (approximately 90 days)
          setSnackbarMessage("Date range is too short for reliable analysis");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          setDateRangeError(true);
          return;
        }

        // Only proceed with settings dispatch and calculation if date range is valid
        setDateRangeError(false);

        // Adjust dates to avoid backend issues
        const adjustedDates = adjustDatesForBackendCompatibility(
          localSettings.startDate,
          localSettings.endDate
        );

        dispatch(
          setSettings({
            ...localSettings,
            startDate: adjustedDates.startDate,
            endDate: adjustedDates.endDate,
          })
        );
        dispatch(setCalculated(true));

        // Show appropriate message for valid date ranges
        if (daysDiff < 365) {
          setSnackbarMessage("Comparing returns based on short time periods is unreliable");
          setSnackbarSeverity("warning");
          setOpenSnackbar(true);
          // Keep settings panel open for warning message - user closes manually
        } else {
          setSnackbarMessage("You should calculate first to display the correct data on graphs");
          setSnackbarSeverity("info");
          setOpenSnackbar(true);
          setSettingsExpanded(false);
        }
      };

      const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
          return;
        }
        setOpenSnackbar(false);
      };



    return (
      settingsExpanded && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid
            container
            direction="column"
            spacing={3}
            sx={{ margin: "0 auto", width: "80%" }}
          >
            <Grid
              item
              container
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <Grid item>
                <IconButton
                  sx={{
                    padding: "14px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                  }}
                  onClick={handleSettingsToggle}
                >
                  <CloseIcon sx={{ color: "#008ed1" }} />
                </IconButton>
              </Grid>
            </Grid>
            <Grid
              item
              container
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="subtitle1"
                sx={{ color: "#1D2155", fontWeight: 600 }}
              >
                Simulation Period
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat , sans-serif",
                    fontSize: "14px",
                    color: "#1D2155",
                    fontWeight: "400",
                    marginRight: "8px",
                  }}
                >
                  currency
                </Typography>
                <FormControl sx={{ minWidth: 120 }}>
                  <Select
                    value={localSettings.currency}
                    onChange={handleCurrencyChange}
                    disabled={isLoadingStrategyData}
                    displayEmpty
                    variant="outlined"
                    size="small"
                    sx={{
                      background: isLoadingStrategyData ? "#f5f5f5" : "#fff",
                      fontWeight: 600,
                      color: "#1D2155",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#008ed1",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#008ed1",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#008ed1",
                      },
                    }}
                    IconComponent={isLoadingStrategyData ? () => <CircularProgress size={14} /> : ExpandMoreIcon}
                  >
                    {currencies.map((currency) => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {currency.symbol}({currency.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat , sans-serif",
                    fontSize: "16px",
                    color: "#1D2155",
                    fontWeight: "600",
                  }}
                >
                  Start Date
                </Typography>

                {isLoadingStrategyData ? (
                  <Box sx={{ width: "90%", position: "relative" }}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={56}
                      sx={{ borderRadius: 1 }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: 12,
                        transform: "translateY(-50%)",
                      }}
                    >
                      <CircularProgress size={20} sx={{ color: "#008ED1" }} />
                    </Box>
                  </Box>
                ) : (
                  <DatePicker
                    format="dd/MM/yyyy"
                    minDate={getMinDate()}
                    maxDate={getMaxDate()}
                    sx={{
                      width: "90%",
                      "& .MuiInputBase-root": {
                        background: "white",
                      },
                      "& .MuiButtonBase-root": {
                        color: "#008ED1",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: dateRangeError ? "red" : "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: dateRangeError ? "red" : "rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: dateRangeError ? "red" : "#008ED1",
                        },
                      },
                    }}
                    value={localSettings.startDate}
                    onChange={handleDateChange("startDate")}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat , sans-serif",
                    fontSize: "16px",
                    color: "#1D2155",
                    fontWeight: "600",
                  }}
                >
                  End Date
                </Typography>
                {isLoadingStrategyData ? (
                  <Box sx={{ width: "90%", position: "relative" }}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={56}
                      sx={{ borderRadius: 1 }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: 12,
                        transform: "translateY(-50%)",
                      }}
                    >
                      <CircularProgress size={20} sx={{ color: "#008ED1" }} />
                    </Box>
                  </Box>
                ) : (
                  <DatePicker
                    format="dd/MM/yyyy"
                    minDate={getMinDate()}
                    maxDate={getMaxDate()}
                    sx={{
                      width: "90%",
                      "& .MuiInputBase-root": {
                        background: "white",
                      },
                      "& .MuiButtonBase-root": {
                        color: "#008ED1",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: dateRangeError ? "red" : "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: dateRangeError ? "red" : "rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: dateRangeError ? "red" : "#008ED1",
                        },
                      },
                    }}
                    value={localSettings.endDate}
                    onChange={handleDateChange("endDate")}
                  />
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                lg={2.5}
                xl={2}
                sx={{ display: "flex", paddingTop: "34px!important" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.rebalancingPeriod !== 0}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setLocalSettings((prev) => ({
                          ...prev,
                          rebalancingPeriod: isChecked ? 1 : 0,
                          transactionFeeType: isChecked
                            ? prev.transactionFeeType
                            : "-",
                          transactionFeeValue: isChecked
                            ? prev.transactionFeeValue
                            : "",
                        }));
                      }}
                    />
                  }
                  label="Rebalancing"
                />
              </Grid>

              <Grid item xs={12} md={2} sx={{ display: "flex" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "60%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Montserrat , sans-serif",
                      fontSize: "16px",
                      color: "#1D2155",
                      fontWeight: "600",
                    }}
                  >
                    Months
                  </Typography>

                  <TextField
                    type="number"
                    inputProps={{ min: 1, max: 10 }}
                    value={localSettings.rebalancingPeriod}
                    onChange={handleInputChange("rebalancingPeriod")}
                    sx={{ background: "white" }}
                    disabled={localSettings.rebalancingPeriod === 0}
                    fullWidth
                  />
                </div>
              </Grid>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle1"
                sx={{ color: "#1D2155", fontWeight: 600 }}
              >
                Extra
              </Typography>
            </Grid>
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "16px",
                    color: "#1D2155",
                    fontWeight: "600",
                  }}
                >
                  Transaction Fee
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ background: "white" }}
                  type="number"
                  value={localSettings.transactionFeeValue}
                  onChange={handleInputChange("transactionFeeValue")}
                  disabled={
                    localSettings.transactionFeeType === "-" ||
                    localSettings.rebalancingPeriod === 0
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Select
                          value={localSettings.transactionFeeType}
                          onChange={handleInputChange("transactionFeeType")}
                          disabled={localSettings.rebalancingPeriod === 0}
                          variant="standard"
                          sx={{ width: "60px" }}
                        >
                          <MenuItem value="-">-</MenuItem>
                          <MenuItem value={getCurrentCurrency().symbol}>{getCurrentCurrency().symbol}</MenuItem>
                          <MenuItem value="%">%</MenuItem>
                        </Select>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {localSettings.transactionFeeType}
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat , sans serif",
                    fontSize: "16px",
                    color: "#1D2155",
                    fontWeight: "500",
                  }}
                >
                  per transaction
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  sx={{
                    fontFamily: "Montserrat , sans-serif",
                    fontSize: "16px",
                    color: "#1D2155",
                    fontWeight: "600",
                  }}
                >
                  Initial investment <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box position="relative">
                  <TextField
                    sx={{
                      background: "white",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: investmentError
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: investmentError
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: investmentError ? "red" : "#1976d2",
                        },
                      },
                    }}
                    type="number"
                    //   label="Investment Amount"
                    error={!!investmentError}
                    value={localSettings.investmentAmount}
                    onChange={handleInputChange("investmentAmount")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{getCurrentCurrency().symbol}</InputAdornment>
                      ),
                    }}
                    required
                    fullWidth
                  />
                </Box>
                {investmentError && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "red",
                      mt: 1,
                      display: "block",
                    }}
                  >
                    {investmentError}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid item container justifyContent="flex-start" pt={2} pb={3}>
              <Grid
                item
                mr={isMobile ? 0 : 2}
                mb={isMobile ? 2 : 0}
                xs={12}
                lg={2}
              >
                <Button
                  variant="contained"
                  disabled={isLoadingStrategyData}
                  sx={{
                    background: isLoadingStrategyData ? "#cccccc" : "#008ed1",
                    color: "#fff",
                    width: "100%",
                    padding: isMobile ? "12px 20px" : "16px 48px",
                    textTransform: "capitalize",
                    borderRadius: "0px",
                  }}
                  onClick={handleRecalculate}
                >
                  {isLoadingStrategyData ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      Loading dates...
                    </Box>
                  ) : (
                    "Calculate"
                  )}
                </Button>
              </Grid>
              <Grid item xs={12} lg={2}>
                <Button
                  variant="outlined"
                  sx={{
                    background: "#fff",
                    color: "#008ed1",
                    width: "100%",
                    padding: isMobile ? "12px 20px" : "16px 48px",
                    textTransform: "capitalize",
                    borderRadius: "0px",
                  }}
                  onClick={handleSettingsToggle}
                >
                  Cancel
                </Button>
                <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
          sx={{
            width: "100%",
            backgroundColor: snackbarSeverity === "error" ? "#f44336" : snackbarSeverity === "warning" ? "#ff9800" : "#008ed1",
            color: "white"
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
              </Grid>
            </Grid>
          </Grid>
        </LocalizationProvider>
      )
    );
  }
);

export default StrategySettings;
